import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { customerAccountAPI, customersAPI, salesAPI } from '../services/api';
import { buildReceiptPreviewPdfFromElement } from '../lib/receiptPreviewPdf';
import { toast } from 'sonner';
import { Search, Loader2, Wallet, Euro, Users, CheckCircle2, Receipt, FileText, ShoppingCart, Mail, TicketPercent, Copy } from 'lucide-react';

const PAYMENT_LABELS = {
  cash: 'Dinheiro',
  card: 'Cartão',
  multibanco: 'Multibanco',
  transfer: 'Transferência',
  mbway: 'MBWay',
  account_current: 'Conta Corrente',
  voucher: 'Voucher',
  other: 'Outro',
  credit_card: 'Cartão Crédito',
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const fmtEUR = (v) => `€${Number(v || 0).toFixed(2)}`;

const fmtDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

export default function CustomerAccountPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [accountView, setAccountView] = useState('monetary');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNote, setPaymentNote] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  const [selectedSale, setSelectedSale] = useState(null);
  const [showSaleDetail, setShowSaleDetail] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [saleEmail, setSaleEmail] = useState('');
  const [sendingSaleEmail, setSendingSaleEmail] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedSaleId, setCompletedSaleId] = useState(null);
  const [isFiscalSale, setIsFiscalSale] = useState(false);
  const [receiptPdfUrl, setReceiptPdfUrl] = useState('');
  const [receiptPdfLoading, setReceiptPdfLoading] = useState(false);
  const receiptPreviewRef = useRef(null);

  const loadOpenItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await customerAccountAPI.getOpenItems();
      setItems(res?.data?.items || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar conta corrente');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOpenItems();
  }, [loadOpenItems]);

  const viewItems = useMemo(() => {
    if (accountView === 'all') return items;
    return items.filter((item) => (
      Math.abs(Number(item.open_amount || 0)) > 0.005 ||
      Math.abs(Number(item.voucher_credit || 0)) > 0.005
    ));
  }, [items, accountView]);

  const filteredItems = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return viewItems;
    return viewItems.filter((item) => {
      const hay = normalizeText([
        item.customer_name,
        item.customer_id,
        item.entries_count,
        item.open_amount,
      ].join(' '));
      return hay.includes(q);
    });
  }, [viewItems, search]);

  const accountSummary = useMemo(() => ({
    debit: viewItems.reduce((acc, item) => acc + Number(item.open_amount || 0), 0),
    credit: viewItems.reduce((acc, item) => acc + Number(item.voucher_credit || 0), 0),
    vouchers: viewItems.reduce((acc, item) => acc + Number(item.active_vouchers || 0), 0),
    percentageVouchers: viewItems.reduce((acc, item) => acc + Number(item.percentage_vouchers || 0), 0),
  }), [viewItems]);

  const openCustomerDetail = async (customerId) => {
    try {
      setSelectedCustomer(customerId);
      setShowDetail(true);
      setDetailLoading(true);
      const res = await customerAccountAPI.getCustomerDetail(customerId);
      setDetailData(res?.data || null);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao abrir detalhe da conta corrente');
    } finally {
      setDetailLoading(false);
    }
  };

  const openRegisterPayment = (entry) => {
    setSelectedEntry(entry);
    setPaymentAmount(String(Number(entry?.open_amount || 0).toFixed(2)));
    setPaymentMethod('cash');
    setPaymentNote('');
    setShowPaymentModal(true);
  };



  const loadReceiptBackendPdfForModal = useCallback(async (saleId) => {
    if (!saleId) {
      setReceiptPdfUrl('');
      return;
    }

    setReceiptPdfLoading(true);
    try {
      const response = await salesAPI.getReceiptPDF(saleId, 'receipt');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const nextUrl = window.URL.createObjectURL(blob);

      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return nextUrl;
      });
    } catch (error) {
      console.error('Erro ao carregar pré-visualização PDF do talão:', error);
      setReceiptPdfUrl('');
      toast.error(error?.response?.data?.detail || 'Erro ao carregar pré-visualização do talão');
    } finally {
      setReceiptPdfLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showReceiptModal && completedSaleId) {
      loadReceiptBackendPdfForModal(completedSaleId);
    }

    if (!showReceiptModal) {
      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return '';
      });
      setReceiptPdfLoading(false);
    }
  }, [showReceiptModal, completedSaleId, loadReceiptBackendPdfForModal]);

  const buildReceiptPreviewPdfPayload = async () => {
    const docNumber =
      receiptData?.sale?.vendus_invoice_number ||
      receiptData?.sale?.ncp_number ||
      completedSaleId ||
      'talao';

    return await buildReceiptPreviewPdfFromElement(
      receiptPreviewRef.current,
      docNumber
    );
  };

  const sendReceiptPreviewEmail = async (saleId, recipientEmail) => {
    const email = String(recipientEmail || '').trim();

    if (!saleId) {
      throw new Error('Venda sem identificador');
    }

    if (!email) {
      throw new Error('Indique um email');
    }

    // Blindagem: o anexo do talão é sempre gerado no backend.
    // Nunca usamos PDF criado a partir da pré-visualização HTML do frontend.
    await salesAPI.sendEmail(saleId, email);
  };

  const loadSaleIntoReceiptModal = async (sale, fallbackEmail = '') => {
    if (!sale?.id) {
      throw new Error('Venda sem identificador');
    }

    const receiptRes = await salesAPI.getReceipt(sale.id);
    const receipt = receiptRes?.data || null;

    setReceiptData(receipt);
    setCompletedSaleId(sale.id);
    setSaleEmail(
      receipt?.sale?.customer_email ||
      sale?.customer_email ||
      fallbackEmail ||
      ''
    );
    setIsFiscalSale(
      !!receipt?.sale?.vendus_invoice_id ||
      String(receipt?.sale?.invoice_type || '').toLowerCase() === 'fiscal'
    );
    setShowReceiptModal(true);

    await new Promise((resolve) => setTimeout(resolve, 300));
    return receipt;
  };

  const openReceiptPreviewPrintWindow = async () => {
    if (!completedSaleId) {
      throw new Error('Venda sem identificador');
    }

    // Blindagem: impressão sempre pelo PDF backend 80mm.
    const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const win = window.open(blobUrl, '_blank');

    if (!win) {
      throw new Error('O navegador bloqueou a abertura da janela de impressão');
    }

    toast.success('Talão backend pronto a imprimir');
  };

  const downloadReceiptPreviewPdf = async () => {
    if (!completedSaleId) {
      throw new Error('Venda sem identificador');
    }

    // Blindagem: descarregar/abrir sempre o PDF backend 80mm.
    const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  const openSaleDetail = async (entry) => {
    try {
      const saleId = entry?.sale_id || entry?.id;
      if (!saleId) {
        toast.error('Venda sem identificador');
        return;
      }

      let fullSale = null;
      let resolvedEmail = '';

      const saleRes = await salesAPI.getById(saleId);
      fullSale = saleRes?.data || null;

      if (fullSale?.customer_email) {
        resolvedEmail = String(fullSale.customer_email || '').trim();
      }

      if (!resolvedEmail && fullSale?.customer_id && customersAPI?.getById) {
        try {
          const customerRes = await customersAPI.getById(fullSale.customer_id);
          resolvedEmail = String(customerRes?.data?.email || '').trim();
        } catch (err) {
          console.error('Erro a resolver email do cliente:', err);
        }
      }

      setSelectedSale(fullSale);
      setSaleEmail(resolvedEmail);
      setShowSaleDetail(true);
    } catch (error) {
      console.error('Erro ao abrir detalhe da venda:', error);
      toast.error('Erro ao abrir detalhe da venda');
    }
  };

  const handleViewFiscalPDF = async (sale, format = 'a4') => {
    setPdfLoading(sale.id + '-' + format);
    try {
      const res = await salesAPI.getInvoicePDF(sale.id, format);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (err) {
      toast.error('Erro ao gerar PDF fiscal');
    }
    setPdfLoading(null);
  };

  const handleDownloadPDF = async (sale) => {
    setPdfLoading(sale.id + '-dl');
    try {
      const res = sale?.vendus_invoice_id
        ? await salesAPI.getInvoicePDF(sale.id, 'a4')
        : await salesAPI.getReceiptPDF(sale.id, 'a4');
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ('Fatura_' + (sale.vendus_invoice_number || sale.ncp_number || sale.id) + '.pdf').replace(/[\s/]/g, '_');
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descarregado');
    } catch (err) {
      toast.error('Erro ao descarregar PDF');
    }
    setPdfLoading(null);
  };

  const handleViewInternalPDF = async (sale, format = 'receipt') => {
    setPdfLoading(sale.id + '-internal-' + format);
    try {
      const res = await salesAPI.getReceiptPDF(sale.id, format === 'receipt' ? 'receipt' : format);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (err) {
      console.error('Erro ao gerar PDF interno:', err);
      toast.error('Erro ao gerar PDF interno');
    }
    setPdfLoading(null);
  };

  const handleViewReceiptModal = async (sale) => {
    setPdfLoading(sale.id + '-receipt-modal');
    try {
      await loadSaleIntoReceiptModal(sale, saleEmail || sale?.customer_email || '');
    } catch (err) {
      console.error('Erro ao abrir talão:', err);
      toast.error('Erro ao abrir talão');
    }
    setPdfLoading(null);
  };

  const handleCopyToCart = (sale) => {
    const items = (sale.items || []).map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      unit_price: item.unit_price,
      quantity: item.quantity,
      barcode: item.barcode || '',
      vat_rate: item.vat_rate || 23,
    }));
    sessionStorage.setItem('copyToCart', JSON.stringify(items));
    toast.success('Itens copiados, a abrir POS');
    navigate('/pos');
  };

  const handleSendSaleEmail = async (sale) => {
    const email = String(saleEmail || '').trim();

    if (!sale?.id) {
      toast.error('Venda sem identificador');
      return;
    }

    if (!email) {
      toast.error('Indique um email para envio');
      return;
    }

    setSendingSaleEmail(true);
    try {
      await loadSaleIntoReceiptModal(sale, email);
      await sendReceiptPreviewEmail(sale.id, email);
      toast.success('Talão enviado por email com sucesso');
    } catch (err) {
      console.error('Erro ao enviar talão por email:', err);
      toast.error(err?.response?.data?.detail || err?.message || 'Erro ao enviar talão por email');
    } finally {
      setSendingSaleEmail(false);
    }
  };

  const handleRegisterPayment = async () => {
    if (!selectedEntry?.id) {
      toast.error('Lançamento de Conta Corrente inválido');
      return;
    }

    const amount = Number.parseFloat(String(paymentAmount || '').replace(',', '.')) || 0;
    if (amount <= 0) {
      toast.error('Indica um valor válido');
      return;
    }

    try {
      setSavingPayment(true);
      await customerAccountAPI.registerPayment(
        selectedEntry.id,
        amount,
        paymentMethod,
        paymentNote
      );

      toast.success('Pagamento registado com sucesso');
      setShowPaymentModal(false);

      if (selectedCustomer) {
        const res = await customerAccountAPI.getCustomerDetail(selectedCustomer);
        setDetailData(res?.data || null);
      }

      await loadOpenItems();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Erro ao registar pagamento');
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Manrope']">Conta Corrente</h1>
          <p className="text-slate-400 mt-1">
            Clientes com vendas faturadas e valores por liquidar
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="card-dark">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">{accountView === 'all' ? 'Contas e vouchers' : 'Clientes com saldo'}</p>
              <p className="text-white text-2xl font-bold">{viewItems.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Débito em aberto</p>
              <p className="text-white text-2xl font-bold">
                {fmtEUR(accountSummary.debit)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <Euro className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Crédito em vouchers</p>
              <p className="text-white text-2xl font-bold">
                {fmtEUR(accountSummary.credit)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/15 flex items-center justify-center">
              <TicketPercent className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Vouchers válidos</p>
              <p className="text-white text-2xl font-bold">{accountSummary.vouchers}</p>
              <p className="text-xs text-slate-500">{accountSummary.percentageVouchers} percentuais</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-dark">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar cliente..."
                className="pl-10 bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <select
              value={accountView}
              onChange={(e) => setAccountView(e.target.value)}
              className="h-10 min-w-[290px] rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              aria-label="Filtrar contas correntes"
            >
              <option value="monetary">Apenas contas com saldo (€)</option>
              <option value="all">Todas as contas e vouchers</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="card-dark">
        <CardContent className="p-0">
          <Table className="table-dark">
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Lançamentos</TableHead>
                <TableHead className="text-right text-slate-700 font-semibold">Em aberto</TableHead>
                <TableHead className="text-right">Crédito voucher</TableHead>
                <TableHead className="text-right">Vouchers</TableHead>
                <TableHead>Último movimento</TableHead>
                <TableHead className="text-right text-slate-700 font-semibold">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    A carregar...
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-400">
                    {accountView === 'all'
                      ? 'Não existem contas correntes nem vouchers válidos.'
                      : 'Não existem clientes com saldo monetário em conta corrente.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => (
                  <TableRow key={item.customer_id}>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-white">{item.customer_name || 'Cliente'}</p>
                        <p className="text-xs text-slate-500">{item.customer_id}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-white font-semibold">
                      {item.entries_count || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-bold text-amber-300">{fmtEUR(item.open_amount)}</span>
                    </TableCell>
                    <TableCell className="text-right font-bold text-emerald-300">{fmtEUR(item.voucher_credit)}</TableCell>
                    <TableCell className="text-right text-white">{item.active_vouchers || 0}</TableCell>
                    <TableCell className="text-slate-300">{fmtDate(item.last_movement_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        onClick={() => openCustomerDetail(item.customer_id)}
                        className="bg-emerald-500 hover:bg-emerald-600"
                      >
                        Abrir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="w-[96vw] max-w-6xl max-h-[90vh] overflow-y-auto p-0" style={{ background:"#f8fafc", border:"1px solid #cbd5e1", color:"#0f172a" }}>
          <DialogHeader className="px-6 pt-6 pb-3 border-b" style={{ borderColor:"#cbd5e1", background:"#f8fafc" }}>
            <DialogTitle className="text-slate-900 text-xl">Conta Corrente</DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              A carregar detalhe...
            </div>
          ) : detailData ? (
            <div className="space-y-5 px-6 py-5" style={{ background:"#f8fafc", color:"#0f172a" }}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <Card className="bg-white border-slate-300 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Cliente</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">
                      {detailData.customer?.name || detailData.entries?.[0]?.customer_name || "Cliente"}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-300 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Crédito disponível</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-1">{fmtEUR(detailData.summary?.voucher_credit || 0)}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {detailData.summary?.active_vouchers || 0} voucher(s), {detailData.summary?.percentage_vouchers || 0} percentual(is)
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-300 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total em aberto</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1">
                      {fmtEUR(detailData.summary?.open_amount ?? detailData.summary?.open_total ?? 0)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-300 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Lançamentos</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {detailData.summary?.entries_count || (detailData.entries || []).length || 0}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="rounded-xl overflow-hidden border border-slate-300 bg-white shadow-sm">
                <Table className="bg-white text-slate-900">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-slate-700 font-semibold">Documento</TableHead>
                      <TableHead className="text-slate-700 font-semibold">Data</TableHead>
                      <TableHead className="text-right text-slate-700 font-semibold">Total</TableHead>
                      <TableHead className="text-right text-slate-700 font-semibold">Pago</TableHead>
                      <TableHead className="text-right text-slate-700 font-semibold">Em aberto</TableHead>
                      <TableHead className="text-slate-700 font-semibold">Estado</TableHead>
                      <TableHead className="text-right text-slate-700 font-semibold">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailData.entries || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          Sem lançamentos.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (detailData.entries || []).map((entry) => (
                        <TableRow
                          key={entry.id || entry.sale_id || entry.sale_number}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => openSaleDetail(entry)}
                        >
                          <TableCell>
                            <div>
                              <p className="font-semibold text-slate-900">
                                {entry.sale_number || entry.sale_id || entry.id}
                              </p>
                              <p className="text-xs text-slate-500">
                                {entry.invoice_type === 'fiscal' ? 'Fatura fiscal' : 'Conta Corrente'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-700">{fmtDate(entry.created_at)}</TableCell>
                          <TableCell className="text-right font-semibold">
                            {fmtEUR(entry.total_amount)}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-700">
                            {fmtEUR(entry.paid_amount)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-amber-600">
                            {fmtEUR(entry.open_amount)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                String(entry.status || '').toLowerCase() === 'closed'
                                  ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-300'
                                  : 'bg-amber-500/15 text-amber-700 border border-amber-300'
                              }
                            >
                              {String(entry.status || '').toLowerCase() === 'closed' ? 'Liquidado' : 'Em aberto'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            {Number(entry.open_amount || 0) > 0 ? (
                              <Button
                                type="button"
                                onClick={() => openRegisterPayment({ ...entry, id: entry.id || entry.sale_id || entry.sale_number })}
                                className="bg-emerald-500 hover:bg-emerald-600"
                              >
                                Registar pagamento
                              </Button>
                            ) : (
                              <span className="inline-flex items-center gap-2 text-emerald-700 font-medium">
                                <CheckCircle2 className="w-4 h-4" />
                                Fechado
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="rounded-xl overflow-hidden border border-slate-300 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
                  <TicketPercent className="h-5 w-5 text-violet-600" />
                  <h3 className="font-bold text-slate-900">Vouchers válidos do cliente</h3>
                </div>
                <Table className="bg-white text-slate-900">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Validade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailData.vouchers || []).length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="py-7 text-center text-slate-500">Sem vouchers válidos.</TableCell></TableRow>
                    ) : (detailData.vouchers || []).map((voucher) => (
                      <TableRow key={voucher.id || voucher.code}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">{voucher.code}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                              title="Copiar código do voucher"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(String(voucher.code || ''));
                                  toast.success('Código do voucher copiado');
                                } catch {
                                  toast.error('Não foi possível copiar o código');
                                }
                              }}
                            >
                              <Copy className="h-4 w-4" />
                              <span className="ml-1">Copiar</span>
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {voucher.voucher_type === 'return' ? 'Devolução/crédito' : 'Voucher'}
                          {voucher.parent_voucher_code ? ` · saldo de ${voucher.parent_voucher_code}` : ''}
                        </TableCell>
                        <TableCell className="text-right font-bold text-emerald-700">
                          {voucher.is_percentage ? `${Number(voucher.value || 0).toFixed(2)}%` : fmtEUR(voucher.value)}
                        </TableCell>
                        <TableCell className="text-slate-700">{voucher.valid_until ? fmtDate(voucher.valid_until) : 'Sem limite'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-slate-500">Sem detalhe disponível.</div>
          )}

          <DialogFooter className="px-6 pb-6 pt-2 bg-[#f8fafc]">
            <Button
              variant="outline"
              onClick={() => setShowDetail(false)}
              className="border-slate-300 bg-white text-slate-800"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showSaleDetail} onOpenChange={setShowSaleDetail}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedSale?.vendus_invoice_id ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Fiscal</Badge>
              ) : (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Interno</Badge>
              )}
              {selectedSale?.vendus_invoice_number || selectedSale?.ncp_number || 'Detalhes da Venda'}
            </DialogTitle>
          </DialogHeader>

          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm bg-slate-800/50 rounded-lg p-4">
                <div><span className="text-slate-400">Data:</span><span className="ml-2 text-white">{fmtDate(selectedSale.completed_at)}</span></div>
                <div><span className="text-slate-400">Pagamento:</span><span className="ml-2 text-white">{PAYMENT_LABELS[selectedSale.payment_method] || selectedSale.payment_method}</span></div>
                <div><span className="text-slate-400">Cliente:</span><span className="ml-2 text-white">{selectedSale.customer_name || 'Consumidor Final'}</span></div>
                {selectedSale.vendus_invoice_number && (
                  <div><span className="text-slate-400">Fatura:</span><span className="ml-2 text-emerald-400 font-mono">{selectedSale.vendus_invoice_number}</span></div>
                )}
              </div>

              <Card className="card-dark">
                <Table className="table-dark">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artigo</TableHead>
                      <TableHead className="text-center">Qtd</TableHead>
                      <TableHead className="text-right">Preço</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedSale.items || []).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-white text-sm">{item.product_name}</TableCell>
                        <TableCell className="text-center text-slate-300">{item.quantity}</TableCell>
                        <TableCell className="text-right text-slate-300">€{(item.unit_price || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right text-white font-medium">€{(item.total || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>

              <div className="space-y-1 text-sm px-2">
                {(selectedSale.discount_total || 0) > 0 && (
                  <div className="flex justify-between text-red-400"><span>Descontos:</span><span>-€{selectedSale.discount_total.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-slate-300"><span>IVA:</span><span>€{(selectedSale.vat_total || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-lg font-bold text-white border-t border-slate-600 pt-2 mt-2"><span>TOTAL:</span><span>€{(selectedSale.total || 0).toFixed(2)}</span></div>
              </div>
            </div>
          )}

          {selectedSale && (
            <div className="space-y-3 pt-2">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Enviar por email</label>
                <Input
                  type="email"
                  value={saleEmail}
                  onChange={(e) => setSaleEmail(e.target.value)}
                  placeholder="Email do cliente"
                  className="bg-slate-800/60 border-slate-700 text-white"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Se o cliente tiver email registado, aparece preenchido automaticamente.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2 pt-2">
            {selectedSale?.vendus_invoice_id ? (
              <>
                <Button onClick={() => handleViewFiscalPDF(selectedSale)} className="bg-emerald-600 hover:bg-emerald-700">
                  <FileText className="w-4 h-4 mr-2" />Ver Fatura
                </Button>
                <Button variant="outline" onClick={() => handleViewReceiptModal(selectedSale)} className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
                  <Receipt className="w-4 h-4 mr-2" />Talão
                </Button>
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedSale)} className="border-slate-700">
                  <FileText className="w-4 h-4 mr-2" />Download
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => handleViewReceiptModal(selectedSale)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Receipt className="w-4 h-4 mr-2" />Imprimir Talão
                </Button>
                <Button variant="outline" onClick={() => handleViewInternalPDF(selectedSale, 'a4')} className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
                  <FileText className="w-4 h-4 mr-2" />PDF A4
                </Button>
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedSale)} className="border-slate-700">
                  <FileText className="w-4 h-4 mr-2" />Download
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={() => handleSendSaleEmail(selectedSale)}
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              disabled={sendingSaleEmail}
            >
              {sendingSaleEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />Enviar por email
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="border-slate-700"
              onClick={() => { handleCopyToCart(selectedSale); setShowSaleDetail(false); }}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />Copiar Carrinho
            </Button>
            <Button variant="outline" onClick={() => setShowSaleDetail(false)} className="border-slate-700">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="modal-light w-[min(96vw,640px)] max-w-[640px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-400" />
              Talão de Venda
            </DialogTitle>
          </DialogHeader>

          {receiptPdfUrl ? (
            <div className="bg-white rounded-lg overflow-hidden mx-auto border border-slate-200" style={{ width: '88mm', maxWidth: '100%', height: '72vh' }}>
              {/* Pré-visualização oficial do talão backend: igual ao PDF de impressão/email */}
              <iframe
                title="Pré-visualização do talão PDF"
                src={receiptPdfUrl}
                className="w-full h-full border-0 bg-white"
              />
            </div>


          ) : receiptPdfLoading ? (
            <div className="bg-white rounded-lg mx-auto border border-slate-200 flex flex-col items-center justify-center gap-3" style={{ width: '88mm', maxWidth: '100%', height: '72vh' }}>
              <Loader2 className="w-7 h-7 animate-spin text-emerald-600" />
              <p className="text-sm text-slate-700">A carregar talão oficial...</p>
            </div>


          ) : receiptData && (
            <div ref={receiptPreviewRef} className="bg-white text-black p-4 rounded-lg font-mono text-sm w-[80mm] mx-auto" id="receipt-content">
              <div className="text-center mb-3">
                {receiptData.company?.logo_url && (
                  <img
                    src={receiptData.company.logo_url}
                    alt="Logo"
                    className="h-14 mx-auto mb-2 object-contain"
                  />
                )}
                <p className="font-bold text-[17px] leading-tight whitespace-nowrap tracking-[-0.2px]">{receiptData.company?.name || 'Ervanária'}</p>
                {receiptData.company?.address && (
                  <p className="text-[11px] leading-tight text-gray-800">{receiptData.company.address}</p>
                )}
                {receiptData.company?.nif && (
                  <p className="text-[11px] leading-tight text-gray-800">NIF: {receiptData.company.nif}</p>
                )}
                {(receiptData.company?.phone || receiptData.company?.email || receiptData.company?.website) && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    {receiptData.company.phone && `Tel: ${receiptData.company.phone}`}
                    {receiptData.company.phone && receiptData.company.email && ' | '}
                    {receiptData.company.email}
                    {(receiptData.company.phone || receiptData.company.email) && receiptData.company.website && ' | '}
                    {receiptData.company.website}
                  </p>
                )}
                {receiptData.company?.capital && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    Capital Social: €{receiptData.company.capital}
                  </p>
                )}
              </div>

              <div className="border-t border-b border-gray-300 py-2 my-2 text-left">
                <p className="text-[11px] font-medium text-black">
                  Fatura Venda {receiptData.sale?.vendus_invoice_number || receiptData.sale?.ncp_number || '-'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
                <div className="leading-tight">
                  <div className="text-gray-700">Data:</div>
                  <div className="text-black font-medium">
                    {receiptData.sale?.completed_at ? new Date(receiptData.sale.completed_at).toLocaleDateString('pt-PT') : '-'}
                  </div>
                </div>
                <div className="leading-tight text-right">
                  <div className="text-gray-700">Op:</div>
                  <div className="text-black font-medium">{receiptData.sale?.operator_name || '-'}</div>
                </div>
              </div>

              <div className="text-[11px] mb-2 leading-tight">
                <div><span className="text-gray-700">Cliente:</span> <span className="font-medium text-black">{receiptData.sale?.customer_name || 'Consumidor Final'}</span></div>
                <div><span className="text-gray-700">Saldo Pontos Conta Cliente:</span> <span className="font-medium text-black">{receiptData.sale?.customer_points ?? '-'}</span></div>
              </div>

              <div className="mt-3 pt-2 border-t border-gray-300 text-[11px] text-center">
                <p>
                  Pagamento: {
                    {
                      cash: 'Dinheiro',
                      card: 'Cartão',
                      mbway: 'MBWay',
                      multibanco: 'Multibanco',
                      transfer: 'Transferência',
                      account_current: 'Conta Corrente',
                      voucher: 'Voucher',
                      other: 'Outro',
                      credit_card: 'Cartão Crédito'
                    }[receiptData.sale?.payment_method] || receiptData.sale?.payment_method
                  }
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2 px-1">
            <label className="text-slate-700 text-sm">Enviar por email</label>
            <Input
              type="email"
              value={saleEmail}
              onChange={(e) => setSaleEmail(e.target.value)}
              placeholder="Email do cliente"
              className="bg-white border-slate-300 text-slate-900 w-full"
              style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
            />
          </div>

          <DialogFooter className="gap-2 grid grid-cols-1 md:grid-cols-2">
            <Button
              variant="outline"
              onClick={() => setShowReceiptModal(false)}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              Fechar
            </Button>

            <Button
              variant="outline"
              onClick={() => handleSendSaleEmail(selectedSale)}
              disabled={sendingSaleEmail || !completedSaleId}
              className="w-full border-blue-500 text-blue-700 hover:bg-blue-50"
            >
              {sendingSaleEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A enviar...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar por email
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await downloadReceiptPreviewPdf();
                  toast.success('Talão PDF aberto no navegador');
                } catch (error) {
                  console.error('Erro ao gerar talão PDF:', error);
                  toast.error(error?.message || 'Erro ao gerar PDF');
                }
              }}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              <FileText className="w-4 h-4 mr-2" />
              Talão PDF
            </Button>

            <Button
              onClick={async () => {
                try {
                  await openReceiptPreviewPrintWindow();
                } catch (error) {
                  console.error('Erro ao imprimir talão:', error);
                  toast.error(error?.message || 'Erro ao abrir talão para impressão');
                }
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="modal-light w-[95vw] max-w-lg">
          <DialogHeader className="px-6 pt-6 pb-3 border-b" style={{ borderColor:"#cbd5e1", background:"#f8fafc" }}>
            <DialogTitle className="text-slate-900 text-xl">Registar pagamento</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-300 bg-slate-50 p-4">
              <p className="text-sm text-slate-600">Em aberto</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">
                {fmtEUR(selectedEntry?.open_amount || 0)}
              </p>
            </div>

            <div>
              <Label>Valor recebido</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="mt-2 bg-white border-slate-300 text-slate-900"
              />
            </div>

            <div>
              <Label>Método</Label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="mt-2 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-slate-900 outline-none"
              >
                {Object.entries(PAYMENT_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Nota</Label>
              <Input
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                placeholder="Opcional"
                className="mt-2 bg-white border-slate-300 text-slate-900"
              />
            </div>
          </div>

          <DialogFooter className="px-6 pb-6 pt-2 bg-[#f8fafc]">
            <Button
              variant="outline"
              onClick={() => setShowPaymentModal(false)}
              className="border-slate-300 bg-white text-slate-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRegisterPayment}
              disabled={savingPayment}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {savingPayment ? 'A guardar...' : 'Guardar pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
